import { API } from "@swivl-mono/api-helper";
import { LocationMatrixHelper, filterClasses } from "@swivl-mono/helpers";
import {
  ColumnMappingPreview,
  DOCUMENT_UPLOAD_ACCEPT,
  DocumentUploadInterface,
  LocationsMatrix,
  extensionOf,
  fileKindFromName,
  isSupportedUpload
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Checkbox, Spinner } from "@swivl-mono/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { CgSpinner } from "react-icons/cg";
import { FiEye, FiRefreshCw } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import { LocationMatrixPicker } from "../../../Components/LocationMatrixPicker/LocationMatrixPicker";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { ColumnMappingModal } from "./ColumnMappingModal";

// Tabular formats the pre-scan column-mapping modal applies to — mirrors the server's
// isSpreadsheetSource (DocumentIngest.Service.ts) so the pill/gate agree with what Scan itself treats as a sheet.
function isTabularUpload(source: string): boolean {
  const kind = fileKindFromName(source);
  return kind === "xlsx" || kind === "csv";
}

// buildColumnMappingPreview runs server-side in the background after upload; these bound how long
// (and how often) this page keeps re-checking for it before giving up on a file that turns out to
// have nothing to build (unparseable/corrupt) rather than polling that file forever.
const COLUMN_MAPPING_RETRY_MS = 2000;
const MAX_COLUMN_MAPPING_ATTEMPTS = 15; // ~30s of retrying at COLUMN_MAPPING_RETRY_MS

// A column/field that ended up unmapped ("ignored") or still fighting over the same target
// ("duplicate") is why the trigger pill exists at all — a file where every column matched
// confidently gets no button (nothing for the operator to do). A server-computed warning (e.g.
// no identifier column mapped at all) counts too, even when every individual field otherwise
// matched confidently — without this, a sheet with zero identifier columns could sail through
// Scan with no review pill and no block at all.
function columnMappingNeedsReview(preview: ColumnMappingPreview | null | undefined): boolean {
  if (!preview) return false;
  return preview.mapping.fields.some((field) => field.state === "ignored" || field.state === "duplicate") || preview.warnings.length > 0;
}

// Only an UNCONFIRMED file that actually needs review blocks Scan — a file with nothing to review
// never needed a confirm step, and a confirmed file already got the operator's sign-off.
function columnMappingBlocksScan(preview: ColumnMappingPreview | null | undefined): boolean {
  return columnMappingNeedsReview(preview) && !preview?.mapping.confirmedAt;
}

// The review states the landing table renders (distinct from the raw upload status).
// `scanFailed` is our side failing (LLM provider, storage) and is retryable; `failed` is a file we
// genuinely can't read. Telling them apart keeps a provider outage from blaming the file (ENG-3324).
type DocStatus = "scanning" | "ready" | "partial" | "applied" | "scanFailed" | "failed";

function docStatusOf(upload: DocumentUploadInterface): DocStatus {
  if (upload.status === "extracting") return "scanning";
  if (upload.status === "failed") return "scanFailed";
  if (upload.status === "needs-llm-fallback") return "failed";
  const started =
    (upload.bulkMapping?.appliedLocationIds?.length ?? 0) > 0 || (upload.structuredFindings?.findings.some((finding) => finding.appliedAt) ?? false);
  // "Applied" requires a sign-off (confirmedAt), something actually written, AND nothing still
  // unapproved (hasUnapproved, computed live server-side for a confirmed upload) — pressing Done
  // without approving everything must not read as fully applied. Any other combination (signed
  // off with nothing approved, approved things but not yet signed off, or signed off with
  // something still pending) reads as "in progress" instead.
  if (upload.confirmedAt && started && !upload.hasUnapproved) return "applied";
  if (upload.confirmedAt || started) return "partial";
  return "ready";
}

function relativeTime(value: Date | string | null): string {
  if (!value) return "";
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// One-line result summary after a drop.
function uploadSummary(uploaded: number, skipped: string[], failed: string[]): string {
  const parts: string[] = [];
  if (uploaded > 0) parts.push(`Added ${uploaded} to the queue.`);
  if (skipped.length > 0) parts.push(`Skipped ${skipped.length} unsupported (${skipped.join(", ")}).`);
  if (failed.length > 0) parts.push(`${failed.length} failed to upload (${failed.join(", ")}), try again.`);
  return parts.join(" ") || "Nothing to upload.";
}

export function DocumentIngestView(): React.ReactElement {
  const orgId = Member.orgId;
  const navigate = useNavigate();
  // Start with NOTHING selected — the operator must choose the scope (a location, a group, or All)
  // before scanning. Defaulting to `{ orgId }` made the picker open pre-set to "All locations".
  const [matrix, setMatrix] = useState<LocationsMatrix>({});
  const [uploads, setUploads] = useState<DocumentUploadInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [locationsInDocument, setLocationsInDocument] = useState(false);
  // Pre-scan column-mapping preview per queued tabular file (built eagerly server-side on upload) —
  // keyed by upload id so the trigger pill has a real count before the modal is ever opened.
  const [columnMappings, setColumnMappings] = useState<Record<number, ColumnMappingPreview | null>>({});
  const [mappingModalUploadId, setMappingModalUploadId] = useState<number | null>(null);
  // buildColumnMappingPreview runs in the BACKGROUND after upload (S3 fetch + parse + a possible LLM
  // classify call) — it's routinely still running by the time this page asks for it. A doc's first
  // `null` here means "not ready yet", not "nothing to show" — must keep retrying, or the pill (and
  // Scan's gate on it) never appears at all. Gives up after MAX_COLUMN_MAPPING_ATTEMPTS in case the
  // file genuinely has no preview to build (unparseable/corrupt).
  const columnMappingAttemptsRef = useRef<Record<number, number>>({});

  const isOrgWide = matrix.orgId === orgId;
  // Memoized so the array reference is stable across renders that don't actually change `uploads` —
  // the column-mapping poll effect below depends on `queue`, and an unstable reference would reset
  // its retry timer on every unrelated re-render (typing, toggling the checkbox, etc.) instead of
  // only when the queue's real contents change.
  const queue = useMemo(() => uploads.filter((upload) => upload.status === "uploaded"), [uploads]);
  const documents = useMemo(() => uploads.filter((upload) => upload.status !== "uploaded"), [uploads]);
  const anyProcessing = uploads.some((upload) => upload.status === "extracting");
  const wasScanningRef = useRef(false);

  useEffect(() => {
    const pending = queue.filter((doc) => {
      if (!isTabularUpload(doc.source) || columnMappings[doc.id]) return false;
      const attempts = columnMappingAttemptsRef.current[doc.id] ?? 0;
      return attempts < MAX_COLUMN_MAPPING_ATTEMPTS;
    });
    if (pending.length === 0) return;
    let active = true;
    const timer = setTimeout(
      () => {
        (async () => {
          const entries = await Promise.all(
            pending.map(async (doc): Promise<[number, ColumnMappingPreview | null]> => {
              columnMappingAttemptsRef.current[doc.id] = (columnMappingAttemptsRef.current[doc.id] ?? 0) + 1;
              try {
                return [doc.id, await API.DocumentIngest.getColumnMapping(orgId, doc.id)];
              } catch (error) {
                console.error(`DocumentIngest: failed to load column mapping for upload ${doc.id}`, error);
                return [doc.id, null];
              }
            })
          );
          if (active) setColumnMappings((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        })();
      },
      // First check fires quickly; retries space out so a slow (LLM-involving) preview build isn't
      // hammered with requests while it's still running.
      pending.every((doc) => columnMappingAttemptsRef.current[doc.id] === undefined) ? 400 : COLUMN_MAPPING_RETRY_MS
    );
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [queue, orgId, columnMappings]);

  const loadUploads = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const records = await API.DocumentIngest.listUploads(orgId);
        setUploads(records);
      } catch (error) {
        // Silent (background poll) failures still get logged — just no toast spam every 3s.
        if (!silent) AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t load documents, please try again.");
        else console.error("DocumentIngest: background upload-list poll failed", error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orgId]
  );

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
    loadUploads();
  }, [loadUploads]);

  // Poll while any scan runs so `scanning` → `ready` flips without a reload.
  useEffect(() => {
    if (!uploads.some((upload) => upload.status === "extracting")) return;
    const timer = setTimeout(() => loadUploads(true), 3000);
    return () => clearTimeout(timer);
  }, [uploads, loadUploads]);

  useEffect(() => {
    if (anyProcessing) wasScanningRef.current = true;
    else if (wasScanningRef.current) {
      wasScanningRef.current = false;
      setMessage("");
    }
  }, [anyProcessing]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setIsUploading(true);
      const skipped: string[] = [];
      const failed: string[] = [];
      let uploaded = 0;
      try {
        for (const file of files) {
          if (!isSupportedUpload(file.name)) {
            skipped.push(file.name);
            await API.DocumentIngest.getUploadUrl(orgId, file.name); // records the rejected attempt (telemetry)
            continue;
          }
          try {
            const presigned = await API.DocumentIngest.getUploadUrl(orgId, file.name);
            if (!presigned.supported || !presigned.uploadUrl || !presigned.s3Key) {
              skipped.push(file.name);
              continue;
            }
            const put = await fetch(presigned.uploadUrl, { method: "PUT", body: file });
            if (!put.ok) throw new Error(`S3 responded ${put.status}`);
            await API.DocumentIngest.createUpload(orgId, { s3Key: presigned.s3Key, fileName: file.name });
            uploaded += 1;
          } catch (error) {
            // Was silently dropping the real S3/presign error (including the status code thrown
            // above) — the operator only ever saw "N failed" with no way to tell why.
            console.error(`DocumentIngest: upload failed for "${file.name}"`, error);
            failed.push(file.name);
          }
        }
        setMessage(uploadSummary(uploaded, skipped, failed));
        await loadUploads();
      } finally {
        setIsUploading(false);
      }
    },
    [orgId, loadUploads]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({ onDrop: handleDrop, noClick: true, noKeyboard: true, multiple: true });

  const handleScan = async (): Promise<void> => {
    if (queue.length === 0) return;

    let locationIds: number[] | null = null;
    if (!locationsInDocument && !isOrgWide) {
      const groups = LocationsModel.state.groups || [];
      const locations = await LocationMatrixHelper.allLocationsForMatrix(matrix, groups);
      locationIds = locations.map((location) => location.id);
      if (locationIds.length === 0) {
        AlertsModel.errorToast("Select at least one location, or choose All.");
        return;
      }
    }

    setIsScanning(true);
    try {
      const result = await API.DocumentIngest.scanPending(orgId, { locationIds, expectsLocationRoster: locationsInDocument });
      setMessage(`Scanning ${result.scanned} document${result.scanned === 1 ? "" : "s"}…`);
      // Stay on this page — the document shows scan progress in the list; the operator
      // opens the review from its "Review" button once it's ready.
      await loadUploads();
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t start scanning, please try again.");
    } finally {
      setIsScanning(false);
    }
  };

  const handleRemove = async (id: number): Promise<void> => {
    try {
      await API.DocumentIngest.deleteUpload(orgId, id);
      setMessage("");
      await loadUploads(true);
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t remove that file, please try again.");
    }
  };

  const handleRetry = async (id: number): Promise<void> => {
    try {
      await API.DocumentIngest.retryScan(orgId, id);
      await loadUploads(true); // picks up "extracting", which starts the progress poll
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t retry that scan, please try again.");
    }
  };

  const review = (uploadId: number): void => navigate(`bulk/${uploadId}`);

  // Tabular files whose column-mapping preview might still land a result (no preview yet, retries
  // not exhausted) — the SAME set backing the tile's "Checking columns…" pill, reused here so Scan's
  // gate can't diverge from what the pill shows. Without this, Scan stayed clickable for the few
  // seconds between upload and the first successful poll, since a not-yet-loaded preview and a
  // confirmed-clean one both read as "doesn't need review."
  const stillCheckingIds = new Set(
    queue
      .filter(
        (doc) =>
          isTabularUpload(doc.source) && !columnMappings[doc.id] && (columnMappingAttemptsRef.current[doc.id] ?? 0) < MAX_COLUMN_MAPPING_ATTEMPTS
      )
      .map((doc) => doc.id)
  );
  const hasTabularQueued = queue.some((doc) => isTabularUpload(doc.source));

  return (
    <div className="mx-auto flex max-w-[1360px] flex-col gap-10 px-8 py-10">
      <div className="flex items-start justify-between gap-8">
        <div>
          <h1 className="font-poppins text-[32px] leading-tight font-semibold text-grey-900">Document Upload</h1>
          <p className="mt-1.5 max-w-[640px] text-[14px] text-grey-600">
            Check the column-mapping prompt on your file to confirm columns line up before scanning.
            {hasTabularQueued && " For a spreadsheet, be sure at least one column identifies the location: FMS ID, address, location name."}
          </p>
        </div>
      </div>

      {/* Upload card */}
      <div className="overflow-hidden rounded-md border border-grey-200 bg-white shadow-panel">
        <div
          {...getRootProps()}
          className={filterClasses(
            "m-6 flex flex-col items-center gap-4 rounded-md border border-dashed px-6 py-8 text-center transition-colors",
            isDragActive || queue.length > 0 ? "border-orange-500 bg-orange-50" : "border-grey-400 bg-grey-50"
          )}
        >
          <input {...getInputProps()} accept={DOCUMENT_UPLOAD_ACCEPT} />
          {queue.length > 0 ? (
            <>
              <div className="flex flex-wrap justify-center gap-2">
                {queue.map((doc) => (
                  <StagedTile
                    key={doc.id}
                    fileName={doc.source}
                    preview={columnMappings[doc.id]}
                    checking={stillCheckingIds.has(doc.id)}
                    onOpenMapping={() => setMappingModalUploadId(doc.id)}
                    onRemove={() => handleRemove(doc.id)}
                  />
                ))}
              </div>
              <p className="text-[13px] text-grey-500">
                {queue.length} ready to scan{isDragActive ? " · drop to add more" : ", drag & drop or"}
              </p>
            </>
          ) : (
            <>
              <p className="text-[17px] font-semibold text-grey-900">{isDragActive ? "Drop the files to add them" : "Drag & drop files here"}</p>
              <p className="text-[13px] text-grey-500">PDF · Word · Excel · CSV / TXT · images, up to 25 MB each</p>
            </>
          )}
          <Button color={ButtonColor.WHITE} size={ButtonSize.MEDIUM} onClick={open} disabled={isUploading}>
            {isUploading ? "Uploading…" : "Browse files"}
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-6 border-t border-grey-200 bg-grey-50 px-6 py-5">
          <div>
            <p className="mb-1.5 text-[13px] font-semibold text-grey-700">Applies to</p>
            <div className="flex items-center gap-6">
              <div className="w-[280px]">
                {locationsInDocument ? (
                  <p className="rounded-md border border-grey-300 bg-grey-100 px-3 py-2 text-sm text-grey-500">Read from each document</p>
                ) : (
                  <LocationMatrixPicker matrix={matrix} orgId={orgId} onChange={setMatrix} />
                )}
              </div>
              <div>
                <Checkbox
                  isThemed
                  checked={locationsInDocument}
                  onCheckedChange={(checked) => setLocationsInDocument(checked === true)}
                  label="Locations are specified in the document"
                  classNames={{ label: "text-sm text-grey-900" }}
                />
                {locationsInDocument && (
                  <p className="mt-1.5 max-w-[320px] text-[12px] text-grey-500">
                    Each location should be identifiable by name, address, or FMS/location ID so we can match it to a swivl location.
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3 pb-0.5">
            {message && <span className="text-[13px] text-grey-500">{message}</span>}
            <Button
              color={ButtonColor.ORANGE}
              onClick={handleScan}
              disabled={queue.length === 0 || queue.some((doc) => stillCheckingIds.has(doc.id) || columnMappingBlocksScan(columnMappings[doc.id]))}
              loading={isScanning}
            >
              {isScanning ? "Scanning…" : queue.length > 1 ? `Scan ${queue.length} documents` : "Scan document"}
            </Button>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="flex flex-col gap-4">
        <h2 className="font-poppins text-[22px] font-semibold text-grey-900">Documents</h2>

        <div className="overflow-hidden rounded-md border border-grey-200 bg-white shadow-panel">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-grey-500">
              <Spinner />
              <span className="text-sm">Loading…</span>
            </div>
          ) : documents.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-[15px] font-semibold text-grey-900">No documents yet</p>
              <p className="mt-1 text-[13px] text-grey-500">Upload a file above and scan it to see it here.</p>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,1.4fr)_152px] gap-3 border-b border-grey-200 bg-grey-50 px-4 py-3 text-[11px] font-bold tracking-wider text-grey-500 uppercase">
                <span>Document</span>
                <span>Status</span>
                <span />
              </div>
              {documents.map((upload, index) => (
                <DocumentRow
                  key={upload.id}
                  upload={upload}
                  last={index === documents.length - 1}
                  onReview={() => review(upload.id)}
                  onRemove={() => handleRemove(upload.id)}
                  onRetry={() => handleRetry(upload.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {mappingModalUploadId !== null &&
        (() => {
          const doc = uploads.find((upload) => upload.id === mappingModalUploadId);
          if (!doc) return null;
          return (
            <ColumnMappingModal
              orgId={orgId}
              uploadId={doc.id}
              fileName={doc.source}
              onClose={() => setMappingModalUploadId(null)}
              onChange={(preview) => setColumnMappings((prev) => ({ ...prev, [doc.id]: preview }))}
            />
          );
        })()}
    </div>
  );
}

// Exported for ColumnMappingModal's header (same file-type tile, different context).
export function FileTile({ fileName, size }: { fileName: string; size: number }): React.ReactElement {
  const ext = (extensionOf(fileName) || "file").toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-[4px] bg-grey-100 font-bold tracking-wide text-grey-700"
      style={{ width: size, height: size, fontSize: size <= 32 ? 10 : 11 }}
    >
      {ext.slice(0, 4)}
    </span>
  );
}

// A staged (uploaded, not-yet-scanned) file shown as a removable tile inside the drop box.
interface StagedTileProps {
  fileName: string;
  /** This file's column-mapping preview, when it's tabular — undefined while still loading, null
   * for a non-tabular file (never fetched) or an unparseable one. */
  preview: ColumnMappingPreview | null | undefined;
  /** True while the background preview build might still land a result (tabular, no preview yet,
   * retries not exhausted) — shown as its own pill so "still checking" never looks identical to
   * "nothing to do here," which is exactly what made this feature look broken before. */
  checking: boolean;
  onOpenMapping: () => void;
  onRemove: () => void;
}

function StagedTile({ fileName, preview, checking, onOpenMapping, onRemove }: StagedTileProps): React.ReactElement {
  const handleRemove = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onRemove();
  };
  const needsReview = columnMappingNeedsReview(preview);
  const confirmed = !!preview?.mapping.confirmedAt;
  const fields = preview?.mapping.fields ?? [];
  const unmatchedCount = fields.filter((field) => field.state === "ignored" || field.state === "duplicate").length;
  const mappedCount = fields.filter((field) => field.state !== "ignored").length;

  const handleOpenMapping = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onOpenMapping();
  };

  return (
    <div className="flex min-w-56 items-center gap-2 rounded-md border border-grey-300 bg-white p-2 text-left">
      <FileTile fileName={fileName} size={32} />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-grey-900" title={fileName}>
        {fileName}
      </span>
      {needsReview ? (
        <button
          type="button"
          onClick={handleOpenMapping}
          title={confirmed ? "Reopen column mapping" : "Choose which columns to use"}
          className={filterClasses(
            "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-bold whitespace-nowrap",
            confirmed ? "border-teal-200 bg-teal-50 text-teal-900" : "border-orange-100 bg-orange-50 text-orange-700"
          )}
        >
          {confirmed
            ? `${mappedCount} column${mappedCount === 1 ? "" : "s"} mapped ✓`
            : unmatchedCount > 0
              ? `Review ${unmatchedCount} unmatched column${unmatchedCount === 1 ? "" : "s"}`
              : "Review column mapping"}
        </button>
      ) : (
        checking && (
          <span className="inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border border-grey-200 bg-grey-100 px-2.5 text-[12px] font-bold whitespace-nowrap text-grey-500">
            <CgSpinner className="size-3 animate-spin" /> Checking columns…
          </span>
        )
      )}
      <button
        type="button"
        onClick={handleRemove}
        aria-label={`Remove ${fileName}`}
        title="Remove"
        className="shrink-0 rounded-full p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-grey-900"
      >
        <IoClose className="size-4" />
      </button>
    </div>
  );
}

const STATUS_META: Record<DocStatus, { dot: string; label: string }> = {
  scanning: { dot: "bg-grey-400", label: "Scanning…" },
  ready: { dot: "bg-orange-500", label: "Ready to review" },
  partial: { dot: "bg-yellow-700", label: "Review in progress" },
  applied: { dot: "bg-teal-800", label: "Applied" },
  scanFailed: { dot: "bg-red-600", label: "Scan failed" },
  failed: { dot: "bg-red-600", label: "Couldn’t read" }
};

interface DocumentRowProps {
  upload: DocumentUploadInterface;
  last: boolean;
  onReview: () => void;
  onRemove: () => void;
  onRetry: () => void;
}

function DocumentRow({ upload, last, onReview, onRemove, onRetry }: DocumentRowProps): React.ReactElement {
  const status = docStatusOf(upload);
  const meta = STATUS_META[status];
  const scanPct =
    upload.scanTotalCalls && upload.scanTotalCalls > 0
      ? Math.min(100, Math.round(((upload.scanCompletedCalls ?? 0) / upload.scanTotalCalls) * 100))
      : 0;

  const action: { label: string; color: ButtonColor } | null =
    status === "ready"
      ? { label: "Review", color: ButtonColor.ORANGE }
      : status === "partial"
        ? { label: "Resume review", color: ButtonColor.WHITE }
        : status === "applied"
          ? { label: "View summary", color: ButtonColor.WHITE }
          : null;

  return (
    <div
      className={filterClasses(
        "group grid grid-cols-[minmax(0,3fr)_minmax(0,1.4fr)_152px] items-center gap-3 px-4 py-[18px] hover:bg-grey-50",
        !last && "border-b border-grey-200"
      )}
    >
      {/* Document */}
      <div className="flex min-w-0 items-center gap-3">
        <FileTile fileName={upload.source} size={34} />
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold text-grey-900" title={upload.source}>
            {upload.source}
          </div>
          <div className="text-[12px] text-grey-500">Uploaded {relativeTime(upload.createdAt)}</div>
        </div>
      </div>

      {/* Status */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={filterClasses("size-2 shrink-0 rounded-full", meta.dot)} />
          <span className="text-sm font-semibold text-grey-900">{meta.label}</span>
        </div>
        {status === "scanning" ? (
          <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-grey-200">
            <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${scanPct}%` }} />
          </div>
        ) : (
          <div className="text-[12px] text-grey-500">
            {status === "ready" && upload.scanCompletedAt ? `Scanned ${relativeTime(upload.scanCompletedAt)}` : ""}
            {status === "applied" && upload.confirmedAt ? `Applied ${relativeTime(upload.confirmedAt)}` : ""}
            {status === "scanFailed" ? "Something went wrong on our end, not with the file" : ""}
            {status === "failed" ? "The file couldn’t be read" : ""}
          </div>
        )}
      </div>

      {/* Action */}
      <div className="flex items-center justify-end gap-1">
        {status === "scanning" ? (
          <span className="text-[13px] font-semibold text-grey-500 tabular-nums">{scanPct}%</span>
        ) : status === "scanFailed" ? (
          <Button color={ButtonColor.WHITE} size={ButtonSize.MEDIUM} leftIcon={<FiRefreshCw />} onClick={onRetry}>
            Retry scan
          </Button>
        ) : action ? (
          <Button color={action.color} size={ButtonSize.MEDIUM} leftIcon={<FiEye />} onClick={onReview}>
            {action.label}
          </Button>
        ) : (
          <Button variant={ButtonVariant.GHOST} size={ButtonSize.MEDIUM} onClick={onReview}>
            View
          </Button>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${upload.source}`}
          title="Remove document"
          className="shrink-0 rounded-full p-1.5 text-grey-400 opacity-0 transition group-hover:opacity-100 hover:bg-grey-100 hover:text-grey-900"
        >
          <IoClose className="size-4" />
        </button>
      </div>
    </div>
  );
}
